import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router';
import { Menu, X } from 'lucide-react';
import { useAuth } from '~/hooks/useAuth';
import ThemeToggle from './ThemeToggle';

export default function Navbar() {
  const { isLoggedIn, userEmail, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const openButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const wasOpenRef = useRef(false);

  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY >= 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Focus management: move focus into menu on open, return to toggle on close
  useEffect(() => {
    if (mobileOpen) {
      wasOpenRef.current = true;
      closeButtonRef.current?.focus();
    } else if (wasOpenRef.current) {
      wasOpenRef.current = false;
      openButtonRef.current?.focus();
    }
  }, [mobileOpen]);

  // Escape key dismisses mobile menu
  useEffect(() => {
    if (!mobileOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileOpen]);

  // Logged-in: always solid
  const isSolid = isLoggedIn || scrolled || !isHome;

  const navBackground = isLoggedIn
    ? 'bg-bg-elevated/95 backdrop-blur-[12px] border-b border-border-subtle'
    : isSolid
      ? 'bg-bg-elevated/95 backdrop-blur-[12px] border-b border-border-subtle'
      : 'bg-transparent';

  const navHeight = isLoggedIn ? 'h-14' : 'h-16';

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBackground} ${navHeight} flex items-center`}
      >
        <div className="w-full max-w-content mx-auto px-6 lg:px-10 flex items-center justify-between">
          {/* Left: Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 no-underline"
          >
            <span className="font-heading text-lg font-medium text-text-primary">
              ASE
            </span>
            {!isLoggedIn && (
              <span className="hidden md:inline font-heading text-xs text-text-secondary">
                Agentic Software Engineering
              </span>
            )}
          </Link>

          {/* Right: Desktop */}
          <div className="hidden md:flex items-center gap-6">
            {isLoggedIn ? (
              <>
                <Link
                  to="/dashboard"
                  className="font-body text-sm text-text-secondary hover:text-text-primary transition-colors duration-200 no-underline"
                >
                  Dashboard
                </Link>
                {/* R37: the only route out of a locked state — reachable in every access state, including lapsed */}
                <Link
                  to="/account"
                  className="font-body text-sm text-text-secondary hover:text-text-primary transition-colors duration-200 no-underline"
                >
                  Account
                </Link>
                <span
                  className="font-heading text-sm text-text-secondary truncate max-w-[180px]"
                  title={userEmail}
                >
                  {userEmail}
                </span>
                <button
                  onClick={logout}
                  className="font-body text-sm text-text-secondary hover:text-error transition-colors duration-200"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="font-body text-sm text-text-secondary hover:text-text-primary transition-colors duration-200 no-underline"
                >
                  Login
                </Link>
                <Link
                  to="/login"
                  className="font-heading text-sm font-medium bg-accent-cyan text-bg-primary px-5 py-2 rounded-md hover:bg-accent-hover hover:scale-[1.03] hover:shadow-glow transition-all duration-200 no-underline"
                >
                  Start Free Trial
                </Link>
              </>
            )}
          </div>

          {/* Always-visible cluster: theme picker + mobile hamburger. The
              theme toggle lives here — not inside the mobile menu overlay —
              so its dropdown never has to compete with the overlay's z-100. */}
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <button
              ref={openButtonRef}
              className="md:hidden text-text-primary p-3 -mr-2"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              aria-expanded={mobileOpen}
              aria-controls="mobile-menu"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile overlay — CSS transition instead of framer-motion */}
      <div
        id="mobile-menu"
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        inert={!mobileOpen}
        className={`fixed inset-0 z-[100] bg-bg-primary flex flex-col items-center justify-center gap-8 transition-opacity duration-200 motion-reduce:transition-none ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <button
          ref={closeButtonRef}
          className="absolute top-4 right-4 text-text-primary p-3"
          onClick={() => setMobileOpen(false)}
          aria-label="Close menu"
        >
          <X size={28} />
        </button>

        {isLoggedIn ? (
          <>
            <span className="font-heading text-sm text-text-secondary">
              {userEmail}
            </span>
            <Link
              to="/dashboard"
              className="font-heading text-h2 text-text-primary no-underline"
              onClick={() => setMobileOpen(false)}
            >
              Dashboard
            </Link>
            <Link
              to="/account"
              className="font-heading text-h2 text-text-primary no-underline"
              onClick={() => setMobileOpen(false)}
            >
              Account
            </Link>
            <button
              onClick={() => {
                logout();
                setMobileOpen(false);
              }}
              className="font-heading text-h2 text-text-secondary hover:text-error transition-colors duration-200"
            >
              Logout
            </button>
          </>
        ) : (
          <>
            <Link
              to="/"
              className="font-heading text-h2 text-text-primary no-underline"
              onClick={() => setMobileOpen(false)}
            >
              Home
            </Link>
            <Link
              to="/login"
              className="font-heading text-h2 text-text-primary no-underline"
              onClick={() => setMobileOpen(false)}
            >
              Login
            </Link>
            <Link
              to="/login"
              className="font-heading text-h2 text-accent-cyan no-underline"
              onClick={() => setMobileOpen(false)}
            >
              Start Free Trial
            </Link>
          </>
        )}
      </div>
    </>
  );
}
